import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const routeTitles: Record<string, string> = {
  "/": "Home",
  "/homepage": "Home",
  "/login": "Login",
  "/scorecard-login": "Scorecard Login",
  "/change-password": "Change Password",
  "/error": "Error",
  "/socket-status": "Socket Status",
  "/audio_device_demo": "Audio Device Demo",
  "/prompt-management": "Prompt Management",
  "/tenant_management": "Tenant Management",
  "/tenant_management/demo-setup": "Demo Setup",
  "/restaurant": "Dashboard",
  "/restaurant_audio": "Audio",
  "/restaurant_criteria": "Criteria",
  "/restaurant_admin": "Admin",
  "/restaurant/servers": "Server Management",
  "/restaurant/servers/summary": "Server Summary",
  "/restaurant/servers/trial": "Server Leaderboard",
  "/restaurant/servers/leaderboard": "Server Leaderboard",
  "/restaurant/servers/performance": "Server Performance",
  "/restaurant/servers/combined-performance": "Combined Performance",
  "/restaurant/server-scorecard": "Server Scorecard",
  "/restaurant/server-performance-history": "Performance History",
  "/restaurant/shifts": "Recent Shifts",
  "/restaurant/shifts/transcript": "Shift Transcript",
  "/restaurant/demos": "Demos",
  "/restaurant/mockups": "Server Hub",
  "/restaurant/mockups/my-earnings": "My Earnings",
  "/restaurant/mockups/leaderboard": "Arena Leaderboard",
  "/restaurant/mockups/rewards": "Rewards Store",
  "/restaurant/mockups/shift-replay": "Shift Replay",
  "/restaurant/mockups/my-stats": "My Stats",
  "/restaurant/mockups/team-pulse": "Team Pulse",
  "/restaurant/mockups/wrapped": "Monthly Wrapped",
  "/restaurant/mockups/character": "Character Sheet",
  "/restaurant/mockups/rings": "Daily Rings",
  "/restaurant/mockups/exchange": "Upsell Exchange",
  "/restaurant/mockups/bet": "Bet On Yourself",
  "/restaurant/mockups/whales": "Whale Watch",
  "/restaurant/mockups/tonights-plan": "Tonight's Plan",
  "/restaurant/mockups/live-register": "Live Register",
  "/restaurant/mockups/people-who-get-it": "People Who Believe",
  "/restaurant/mockups/small-wins": "Small Wins",
  "/restaurant/mockups/set-your-number": "$100 Night",
  "/restaurant/mockups/onboarding": "Server Onboarding",
  "/restaurant/mockups/shift-live": "Shift Live",
  "/restaurant/mockups/flash-bounties": "Flash Bounties",
  "/restaurant/mockups/cheat-codes": "Cheat Codes",
  "/restaurant/mockups/gen-bbq-analysis": "Gen BBQ Analysis",
  "/restaurant/mockups/jess-coaching-plan": "Coaching Plan",
  "/restaurant/mockups/roi-calculator": "ROI Calculator",
  "/restaurant/mockups/server-app-styles": "Server App Styles",
  "/restaurant/mockups/server-app-styles-2": "Server App Styles 2",
  "/restaurant/mockups/server-app-styles-3": "Server App Styles 3",
  "/restaurant/mockups/server-app-styles-4": "Server App Styles 4",
  "/restaurant/mockups/server-app-prototype": "Server App Prototype",
  "/restaurant/mockups/duolingo-prototype": "Duolingo Prototype",
  "/admin/use-case": "Use Case Admin",
  "/admin/use-case/new": "New Use Case",
  "/admin/user": "User Admin",
  "/admin/communication": "Communication Admin",
  "/oldmain": "Main",
  "/main": "Feedback",
  "/interview": "Interview",
  "/communication": "Communication",
  "/request": "Requests",
};

// feat-153 marketing-site launch: the public landing page ("/" and "/homepage")
// carries its own <title> and <meta name="description"> (spec acceptance #1/#2),
// overriding the stale "Interview Application" default from public/index.html.
// The title is the full marketing title, with no "Bespoke - " prefix. Setting
// these here (not in a Homepage useEffect) is deliberate: this App-level hook
// runs after child effects and would otherwise clobber a child-set
// title/description.
//
// feat-288-update-testing-language changed MARKETING_TITLE deliberately, to name
// guest experience alongside revenue; it is no longer the feat-153 mockup's exact
// <title> and must not be "restored" to it. MARKETING_DESCRIPTION is still the
// mockup's, verbatim.
const MARKETING_ROUTES = new Set(["/", "/homepage"]);
const MARKETING_TITLE =
  "Bespoke, the revenue and guest experience tool for in-person commerce";
const MARKETING_DESCRIPTION =
  "Bespoke turns the live conversation on your floor into measurable, compounding revenue. Strategy, training, measurement, and testing, finally in one loop.";

function setMetaDescription(content: string) {
  let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

/**
 * Sets document.title based on the current route.
 * For dynamic routes (e.g. /restaurant/servers/:id/trial-analysis),
 * falls back to a cleaned-up version of the pathname.
 */
export default function usePageTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (MARKETING_ROUTES.has(pathname)) {
      document.title = MARKETING_TITLE;
      setMetaDescription(MARKETING_DESCRIPTION);
      return;
    }

    const title = routeTitles[pathname];
    if (title) {
      document.title = `Bespoke - ${title}`;
    } else {
      // For dynamic routes, derive a readable title from the path
      const lastSegment = pathname.split("/").filter(Boolean).pop();
      if (lastSegment) {
        const readable = lastSegment
          .replace(/[-_]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        document.title = `Bespoke - ${readable}`;
      } else {
        document.title = "Bespoke";
      }
    }
  }, [pathname]);
}
